import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PaperClipIcon, InformationCircleIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import RichTextEditor from '../components/RichTextEditor';
import ScrollableDropdown from '../components/ScrollableDropdown';
import { useAuth } from '../context/AuthContext';
import MediaViewer from '../components/MediaViewer';
import AttachmentUploader from '../components/AttachmentUploader';

// ── Ticket type config ────────────────────────────────────────────────────────
const TICKET_TYPES = [
  { value: 'Bug',         label: 'Bug',         tooltip: 'A confirmed defect that needs fixing' },
  { value: 'Observation', label: 'Observation',  tooltip: 'Something unusual that needs investigation' },
  { value: 'Improvement', label: 'Improvement',  tooltip: 'Enhancement to existing functionality' },
];

const TICKET_TYPE_COLORS = {
  '':          { header: 'bg-slate-50',  badge: 'bg-slate-100 text-slate-800 border-slate-200', submit: 'bg-slate-600 hover:bg-slate-700' },
  Bug:         { header: 'bg-red-50',    badge: 'bg-red-100 text-red-800 border-red-200',    submit: 'bg-red-600 hover:bg-red-700' },
  Observation: { header: 'bg-amber-50',  badge: 'bg-amber-100 text-amber-800 border-amber-200', submit: 'bg-amber-600 hover:bg-amber-700' },
  Improvement: { header: 'bg-green-50',  badge: 'bg-green-100 text-green-800 border-green-200', submit: 'bg-green-600 hover:bg-green-700' },
};

const SMART_LABELS = {
  '':          { formTitle: 'Report New Ticket',           titleField: 'Ticket Title',        descPlaceholder: 'Describe the ticket...', submitBtn: 'Submit Ticket' },
  Bug:         { formTitle: 'Report New Bug',              titleField: 'Bug Title',        descPlaceholder: 'Describe the bug...', submitBtn: 'Submit Bug Report' },
  Observation: { formTitle: 'Record New Observation',      titleField: 'Observation Title', descPlaceholder: 'Describe your observation...', submitBtn: 'Submit Observation' },
  Improvement: { formTitle: 'Suggest an Improvement',   titleField: 'Improvement Title', descPlaceholder: 'Describe the improvement you are suggesting...', submitBtn: 'Submit Improvement' },
};

// ── Draft helpers ─────────────────────────────────────────────────────────────
const getDraftKey = (userId, ticketType) => `dev_report_ticket_draft_${userId}_${ticketType}`;

const saveDraftLocal = (userId, ticketType, formData, attachments, draftTimestamp) => {
  try {
    const draft = { formData, attachments, savedAt: draftTimestamp || new Date().toISOString() };
    localStorage.setItem(getDraftKey(userId, ticketType), JSON.stringify(draft));
  } catch { }
};


const clearDraftLocal = (userId, ticketType) => {
  try { localStorage.removeItem(getDraftKey(userId, ticketType)); } catch { }
};

const loadAnyDraftLocal = (userId) => {
  try {
    const types = ['', 'Bug', 'Observation', 'Improvement'];
    let mostRecent = null;
    let foundType = '';
    
    types.forEach(type => {
      const raw = localStorage.getItem(getDraftKey(userId, type));
      if (raw) {
        const d = JSON.parse(raw);
        if (!mostRecent || new Date(d.savedAt) > new Date(mostRecent.savedAt)) {
          mostRecent = d;
          foundType = type;
        }
      }
    });
    return mostRecent ? { ...mostRecent, ticketType: foundType } : null;
  } catch { return null; }
};

const formatDraftAge = (savedAt) => {
  if (!savedAt) return '';
  const diff = Math.floor((Date.now() - new Date(savedAt).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};


const DeveloperReportBug = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const getInitialType = () => {
    const lt = location.state?.ticketType || location.state?.workType;
    if (lt === 'observation') return 'Observation';
    if (lt === 'improvement') return 'Improvement';
    if (lt === 'bug' || lt === 'Bug') return 'Bug';
    return '';
  };

  const [ticketType, setTicketType] = useState(getInitialType);
  const colors = TICKET_TYPE_COLORS[ticketType] || TICKET_TYPE_COLORS[''];
  const labels = SMART_LABELS[ticketType] || SMART_LABELS[''];

  const [developers, setDevelopers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => ({
    title: '',
    description: '',
    application: '',
    module: '',
    category: '',
    severity: '',
    priority: '',
    assignedTo: '',
    environment: '',
    domain: ''
  }));
  const [domains, setDomains] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [customCategory, setCustomCategory] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // Draft state
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftAge, setDraftAge] = useState('');
  const draftTimerRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const isFirstRender = useRef(true);
  const draftDismissed = useRef(false);

  // ── Auto-save ─────────────────────────────────────────────────────────────
  const triggerAutoSave = useCallback(() => {
    if (!user?._id) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      const hasContent = formData.title ||
                        formData.description?.replace(/<[^>]*>/g, '').trim() ||
                        formData.application ||
                        formData.category ||
                        formData.severity ||
                        formData.priority ||
                        ticketType ||
                        attachments.length > 0;

      if (!hasContent) {
        if (user?._id) clearDraftLocal(user._id, ticketType);
        setDraftSavedAt(null);
        setDraftRestored(false);
        return;
      }

      const now = new Date().toISOString();
      saveDraftLocal(user._id, ticketType, formData, attachments, now);
      setDraftSavedAt(now);
    }, 800);
  }, [user?._id, ticketType, formData, attachments]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    triggerAutoSave();
  }, [formData, attachments, triggerAutoSave]);

  // ── Update "X ago" label every 30 seconds ─────────────────────────────────
  useEffect(() => {
    if (!draftSavedAt) return;
    setDraftAge(formatDraftAge(draftSavedAt));
    draftTimerRef.current = setInterval(() => setDraftAge(formatDraftAge(draftSavedAt)), 30000);
    return () => clearInterval(draftTimerRef.current);
  }, [draftSavedAt]);

  // ── Restore draft on mount ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user?._id) return;
    
    // Check for any recent draft regardless of current ticketType selection
    const draft = loadAnyDraftLocal(user._id);
    if (!draft) return;
    
    const urlApplication = location.state?.selectedApplication || new URLSearchParams(location.search).get('application');
    setFormData(prev => ({ ...draft.formData, application: urlApplication || draft.formData.application }));
    if (draft.attachments?.length) setAttachments(draft.attachments);
    if (draft.ticketType) setTicketType(draft.ticketType);
    setDraftSavedAt(draft.savedAt);
    setDraftAge(formatDraftAge(draft.savedAt));
    setDraftRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const handleClearDraft = () => {
    if (user?._id) clearDraftLocal(user._id, ticketType);
    setDraftRestored(false);
    draftDismissed.current = true;
    setTicketType('');
    setFormData({ title: '', description: '', application: '', module: '', category: '', severity: '', priority: '', assignedTo: '', environment: '', domain: '' });
    setAttachments([]);
    setSelectedFiles([]);
    setDraftSavedAt(null);
    setDraftRestored(false);
    toast.success('Draft cleared');
  };

  useEffect(() => {
    fetchDevelopers();
    fetchApplications();
    const applicationParam = new URLSearchParams(location.search).get('application');
    if (location.state?.selectedApplication || applicationParam) {
      setFormData(prev => ({ ...prev, application: location.state?.selectedApplication || applicationParam }));
    }
  }, [location.state, location.search]);

  const fetchDevelopers = async () => {
    try { const { data } = await axios.get('/api/auth/developers'); setDevelopers(data.data); }
    catch (err) { console.error('Failed to fetch developers:', err); }
  };

  const fetchApplications = async () => {
    try { const { data } = await axios.get('/api/applications/for-bug-reporting'); setApplications(data.data); }
    catch (err) { console.error('Failed to fetch applications:', err); toast.error('Failed to load applications'); }
  };

  useEffect(() => {
    if (!formData.application) return;
    axios.get(`/api/applications/${formData.application}`).then(({ data }) => {
      setApplications(prev => {
        const idx = prev.findIndex(a => a._id === data.data._id);
        if (idx === -1) return prev;
        const next = [...prev]; next[idx] = data.data; return next;
      });
    }).catch(() => {});
  }, [formData.application]);

  const filteredDevelopers = useMemo(() => {
    if (!formData.application) return developers;
    const selectedApp = applications.find(app => app._id === formData.application);
    if (!selectedApp) return developers;
    const teamDevIds = new Set();
    if (selectedApp.developerId) teamDevIds.add(selectedApp.developerId._id || selectedApp.developerId);
    if (selectedApp.ownerId) teamDevIds.add(selectedApp.ownerId._id || selectedApp.ownerId);
    if (selectedApp.teamMembers) {
      selectedApp.teamMembers.forEach(member => {
        const isDevRole = member.role === 'DEV' || member.userId?.role === 'developer';
        if (isDevRole && member.userId) teamDevIds.add(member.userId._id || member.userId);
      });
    }
    if (teamDevIds.size === 0) return [];
    return developers.filter(dev => teamDevIds.has(dev._id));
  }, [applications, developers, formData.application]);

  useEffect(() => {
    if (!formData.application) {
      setDomains([]);
      return;
    }
    // Always fetch domains – do NOT gate on selectedApp.enableDomains because
    // that flag is not reliably set when a developer adds domains via the form.
    axios
      .get(`/api/applications/${formData.application}/domains`)
      .then(({ data }) => {
        const activeDomains = (data.data || []).filter(d => d.status === 'Active');
        setDomains(activeDomains);
        if (activeDomains.length === 0) {
          setFormData(prev => ({ ...prev, domain: '' }));
        }
      })
      .catch(err => {
        if (err.response?.status !== 403) console.error('Failed to fetch domains', err);
        setDomains([]);
        setFormData(prev => ({ ...prev, domain: '' }));
      });
    if (!formData.assignedTo && filteredDevelopers.length === 1) {
      setFormData(prev => ({ ...prev, assignedTo: filteredDevelopers[0]._id }));
    }
  }, [filteredDevelopers, formData.application, formData.assignedTo, applications]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'application' && value) {
      const selApp = applications.find(app => app._id === value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value, 
        ...(selApp ? { assignedTo: '' } : {}) 
      }));
    } else if (name === 'assignedTo' && value) {
      const isValid = filteredDevelopers.some(dev => dev._id === value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: isValid ? value : '' 
      }));
      if (!isValid) toast.error('Selected developer is not a member of this application');
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value 
      }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTicketTypeChange = (newType) => {
    setTicketType(newType);
    if (errors.ticketType) setErrors(prev => ({ ...prev, ticketType: '' }));
  };

  const removeAttachment = (idToRemove) => setAttachments(prev => prev.filter(att => (att.id || att._id) !== idToRemove));

  const getAttachmentPreviewUrl = (att) => {
    if (!att) return '';
    if (att.url) return att.url;
    if (att.path) return att.path.startsWith('/') ? att.path : `/${att.path}`;
    return '';
  };

  const getPreviewKind = (url) => {
    const clean = String(url || '').split('?')[0].toLowerCase();
    if (clean.match(/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/)) return 'image';
    if (clean.endsWith('.pdf')) return 'pdf';
    if (clean.match(/\.(mp4|webm|ogg|mov|m4v)$/)) return 'video';
    return 'link';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = 'Please add a title for the ticket.';
    
    // Check if description is empty or just contains whitespace/empty HTML tags
    const plainDescription = formData.description?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!plainDescription) newErrors.description = 'Please add a description for the ticket.';

    if (!formData.application) newErrors.application = 'Please select an application.';
    if (domains.length > 0 && !formData.domain) newErrors.domain = 'Please select a domain.';
    if (!formData.assignedTo) newErrors.assignedTo = 'Please assign the ticket to a developer before submitting.';
    if (!formData.category) newErrors.category = 'Please select a category.';
    if (!formData.severity) newErrors.severity = 'Please select a severity level.';
    if (!formData.priority) {
      newErrors.priority = 'Priority is required for all tickets.';
    }
    if (formData.application) {
      const selApp = applications.find(app => app._id === formData.application);
      if (selApp?.isTeamMember === false) {
        newErrors.application = 'You are not a team member of this application. Ticket reporting is restricted.';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { 
      toast.error('Please fix the validation errors before submitting'); 
      return; 
    }
    setLoading(true);
    try {
      const cleanedTitle = formData.title
        .replace(/^(Tests?|Test)\s+(for|For)\s+(Bug|Observation|Improvement):\s*"?/i, '')
        .replace(/^(Bug|Observation|Improvement):\s*"?/i, '')
        .replace(/"?$/, '')
        .trim();

      const finalTitle = `${ticketType}: ${cleanedTitle}`;

      const payload = {
        ...formData,
        title: finalTitle,
        ticketType,
        category: formData.category === 'Other' ? (customCategory || 'Other') : formData.category
      };

      const formDataToSend = new FormData();
      Object.keys(payload).forEach(key => formDataToSend.append(key, payload[key] || ''));
      formDataToSend.append('attachments', JSON.stringify(attachments.map(att => ({ url: att.url, title: att.title, description: att.description }))));
      selectedFiles.forEach(file => formDataToSend.append('files', file));

      await axios.post('/api/bugs', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (user?._id) clearDraftLocal(user._id, ticketType);
      toast.success('Ticket reported successfully!');
      navigate('/bugs');
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to create ticket');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg">

          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className={`px-6 py-4 border-b border-slate-200 rounded-t-lg ${colors.header} transition-colors duration-300`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{labels.formTitle}</h1>
                <p className="mt-1 text-sm text-gray-600">Fill in the details below to report a new ticket</p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${colors.badge} transition-all duration-300`}>
                {ticketType}
              </span>
            </div>
          </div>

          {/* ── Draft Restored Banner ────────────────────────────────────── */}
          {draftRestored && (
            <div className="mx-6 mt-4 flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <ClockIcon className="h-4 w-4 text-blue-500 shrink-0" />
                <span className="text-sm text-blue-800 font-medium">
                  Draft recovered
                  {draftAge ? <span className="font-normal text-blue-600"> · saved {draftAge}</span> : ''}
                </span>
              </div>
              <button type="button" onClick={handleClearDraft}
                className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 shrink-0 transition-colors"
                title="Discard draft and start fresh">
                <XMarkIcon className="h-3.5 w-3.5" /> Clear Draft
              </button>
            </div>
          )}

          {/* ── Auto-save indicator ─────────────────────────────────────── */}
          {draftSavedAt && !draftRestored && (
            <div className="mx-6 mt-3 flex items-center justify-between gap-1.5 text-xs text-gray-400">
              <div className="flex items-center gap-1.5">
                <ClockIcon className="h-3 w-3" />
                <span>Draft auto-saved {draftAge}</span>
              </div>
              <button type="button" onClick={handleClearDraft}
                className="flex items-center gap-1 font-semibold text-red-500 hover:text-red-700 transition-colors"
                title="Discard current work and start fresh">
                <XMarkIcon className="h-3 w-3" /> Clear Draft
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

              {/* ── Left: Main content ───────────────────────────────── */}
              <div className="md:col-span-8 space-y-6">

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    {labels.titleField} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="title" required
                    value={formData.title} onChange={handleChange}
                    className={`w-full h-11 px-3 border rounded-md bg-white text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 ${errors.title ? 'border-red-500 ring-1 ring-red-500/20' : 'border-gray-300'}`}
                    placeholder={ticketType === 'Bug' ? 'Brief description of the bug' : ticketType === 'Observation' ? 'Summarize your observation' : ticketType === 'Feature' ? 'What feature are you requesting?' : 'What would you like to improve?'}
                  />
                  {errors.title && <p className="mt-1 text-xs text-red-600 font-medium">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                    placeholder={labels.descPlaceholder}
                    minHeight="240px"
                    resizable
                    editorClassName={`bg-[#FAFBFF] ${errors.description ? 'border-red-500 ring-1 ring-red-500/20' : 'border-[#CBD5E1]'}`}
                  />
                  {errors.description && <p className="mt-1 text-xs text-red-600 font-medium">{errors.description}</p>}
                </div>

                {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2 flex items-center">
                    <PaperClipIcon className="h-4 w-4 mr-2" />
                    Attachments
                  </label>
                  <div className="bg-gray-50 p-3 rounded-lg border border-dashed border-slate-300">
                    <AttachmentUploader files={selectedFiles} onChange={setSelectedFiles} />
                  </div>
                  {attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {attachments.map((att) => {
                        const key = att.id || att._id;
                        const url = getAttachmentPreviewUrl(att);
                        return (
                          <div key={key} className="flex items-center justify-between bg-white px-4 py-2 rounded-lg border border-gray-200">
                            <button type="button" onClick={() => setPreviewAttachment({ ...att, __previewUrl: url })} className="truncate text-left">
                              <span className="text-sm font-medium text-blue-700 hover:underline truncate">🔗 {att.title || 'Attachment'}</span>
                            </button>
                            <button type="button" onClick={() => removeAttachment(key)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remove</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Right: Sidebar ───────────────────────────────────── */}
              <div className="md:col-span-4 space-y-4">

                {/* Ticket Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Ticket Type <span className="text-red-500">*</span>
                  </label>
                  <ScrollableDropdown
                    value={ticketType}
                    onChange={(value) => handleTicketTypeChange(value)}
                    options={[
                      { value: '', label: 'Select Ticket Type' },
                      ...TICKET_TYPES
                    ]}
                    placeholder="Select Ticket Type"
                    buttonClassName={`${TICKET_TYPE_COLORS[ticketType]?.badge || 'bg-white'}`}
                  />
                </div>

                {/* Application */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Application <span className="text-red-500">*</span>
                  </label>
                  <ScrollableDropdown
                    value={formData.application}
                    onChange={(value) => handleChange({ target: { name: 'application', value } })}
                    options={applications.map((app) => ({ value: app._id, label: `${app.name} (${app.version})` }))}
                    placeholder="Select Application"
                    error={errors.application}
                    maxMenuHeightClassName="max-h-56"
                  />
                  {formData.application && (
                    <div className="mt-2">
                      <Link to={`/applications/${formData.application}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700">
                        <InformationCircleIcon className="h-4 w-4 mr-1" /> View Application Details
                      </Link>
                    </div>
                  )}
                  {formData.application && applications.find(app => app._id === formData.application)?.isTeamMember === false && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm font-semibold text-yellow-800 flex items-center">
                        <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Access Restricted
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">You are not a team member of this application. Ticket reporting is restricted.</p>
                    </div>
                  )}
                  {errors.application && <p className="mt-2 text-sm text-red-600">{errors.application}</p>}
                </div>

                {/* Domain */}
                {domains.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Domain <span className="text-red-500">*</span></label>
                    <ScrollableDropdown
                      value={formData.domain}
                      onChange={(value) => {
                        handleChange({ target: { name: 'domain', value } });
                        if (errors.domain) setErrors(prev => ({ ...prev, domain: '' }));
                      }}
                      options={domains.map((d) => ({ value: d._id, label: d.name }))}
                      placeholder="Select Domain"
                      error={errors.domain}
                      maxMenuHeightClassName="max-h-56"
                    />
                    {errors.domain && <p className="mt-1 text-sm text-red-600">{errors.domain}</p>}
                  </div>
                )}

                {/* Assigned To */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Assigned To (Developer) <span className="text-red-500">*</span>
                  </label>
                  <ScrollableDropdown
                    value={formData.assignedTo}
                    onChange={(value) => handleChange({ target: { name: 'assignedTo', value } })}
                    options={filteredDevelopers.map((dev) => ({ value: dev._id, label: dev.name }))}
                    placeholder={formData.application ? 'Select Developer' : 'Select Application first'}
                    error={errors.assignedTo}
                    disabled={!formData.application}
                    maxMenuHeightClassName="max-h-44"
                  />
                  {errors.assignedTo && <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>}
                </div>

                {/* Module */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Module <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="module" required
                    value={formData.module} onChange={handleChange}
                    className="w-full h-11 px-3 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600"
                    placeholder="e.g., Login, Dashboard, Payment"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <ScrollableDropdown
                    value={formData.category}
                    onChange={(value) => {
                      handleChange({ target: { name: 'category', value } });
                      if (errors.category) setErrors(prev => ({ ...prev, category: '' }));
                    }}
                    options={[
                      { value: 'Functional', label: 'Functional' },
                      { value: 'UI/UX', label: 'UI/UX' },
                      { value: 'Performance', label: 'Performance' },
                      { value: 'Security', label: 'Security' },
                      { value: 'Integration', label: 'Integration' },
                      { value: 'Data', label: 'Data' },
                      { value: 'Observation', label: 'Observation' },
                      { value: 'Other', label: 'Other' },
                    ]}
                    placeholder="Select Category"
                    maxMenuHeightClassName="max-h-56"
                    buttonClassName={errors.category ? 'bg-red-50 border-red-400' : 'bg-gray-50'}
                  />
                  {formData.category === 'Other' && (
                    <div className="mt-2">
                      <input type="text" value={customCategory} onChange={(e) => setCustomCategory(e.target.value)}
                        placeholder="Enter custom category"
                        className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 text-sm" autoFocus />
                    </div>
                  )}
                  {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
                </div>

                {/* Severity + Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Severity <span className="text-red-500">*</span>
                    </label>
                    <ScrollableDropdown
                      value={formData.severity}
                      onChange={(value) => {
                        handleChange({ target: { name: 'severity', value } });
                        if (errors.severity) setErrors(prev => ({ ...prev, severity: '' }));
                      }}
                      options={[
                        { value: 'Low', label: 'Low' },
                        { value: 'Medium', label: 'Medium' },
                        { value: 'High', label: 'High' },
                        { value: 'Critical', label: 'Critical' },
                      ]}
                      placeholder="Select Severity"
                      buttonHeightClassName="h-11"
                      maxMenuHeightPx={240}
                      maxMenuHeightClassName="max-h-60"
                      buttonClassName={errors.severity ? 'bg-red-50 border-red-400' : 'bg-gray-50'}
                    />
                    {errors.severity && <p className="mt-1 text-xs text-red-600">{errors.severity}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <ScrollableDropdown
                      value={formData.priority}
                      onChange={(value) => {
                        handleChange({ target: { name: 'priority', value } });
                        if (errors.priority) setErrors(prev => ({ ...prev, priority: '' }));
                      }}
                      options={[
                        { value: 'Low', label: 'Low' },
                        { value: 'Medium', label: 'Medium' },
                        { value: 'High', label: 'High' },
                      ]}
                      placeholder="Select Priority"
                      buttonHeightClassName="h-11"
                      maxMenuHeightPx={240}
                      maxMenuHeightClassName="max-h-60"
                      buttonClassName={errors.priority ? 'bg-red-50 border-red-400' : 'bg-gray-50'}
                    />
                    {errors.priority && <p className="mt-1 text-xs text-red-600">{errors.priority}</p>}
                  </div>
                </div>

                {/* Environment */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Environment</label>
                  <input type="text" name="environment"
                    value={formData.environment} onChange={handleChange}
                    className="w-full h-11 px-3 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600"
                    placeholder="e.g., Windows 10, Chrome 120, Production"
                  />
                </div>
              </div>
            </div>

            {/* ── Footer ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                {draftSavedAt && (
                  <><ClockIcon className="h-3.5 w-3.5" /><span>Auto-saved {draftAge}</span></>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button type="button" onClick={() => navigate('/bugs')}
                  className="px-4 h-11 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                  Cancel
                </button>
                <button
                  type="submit"
                  id="dev-submit-ticket-btn"
                  disabled={loading || (formData.application && applications.find(app => app._id === formData.application)?.isTeamMember === false)}
                  className={`px-4 h-11 border border-transparent rounded-md shadow-sm text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ${colors.submit}`}
                >
                  {loading ? 'Submitting...' : labels.submitBtn}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── Attachment Preview Modal ──────────────────────────────────────── */}
      {previewAttachment && (
        <div className="fixed inset-0 z-[70] overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setPreviewAttachment(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-gray-900 truncate">{previewAttachment.title || 'Attachment'}</h3>
                  <p className="text-xs text-gray-500 truncate">{previewAttachment.__previewUrl}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a href={previewAttachment.__previewUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-600 hover:text-blue-700">Open</a>
                  <button type="button" onClick={() => setPreviewAttachment(null)} className="text-gray-500 hover:text-gray-700 px-2" aria-label="Close">✕</button>
                </div>
              </div>
              <div className="p-5">
                {(() => {
                  const url = previewAttachment.__previewUrl;
                  const kind = getPreviewKind(url);
                  if (!url) return <div className="text-sm text-gray-600">No preview available.</div>;
                  if (kind === 'image' || kind === 'video') return <MediaViewer src={url} kind={kind} title={previewAttachment.title} className={kind === 'image' ? 'h-[70vh]' : 'max-h-[70vh]'} />;
                  return <iframe title="Attachment Preview" src={url} className="w-full h-[70vh] border border-gray-200 rounded" />;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperReportBug;
